import { useMemo } from 'react';
import { useUiStore } from '@noor/lib';
import { useRestaurants } from './hooks';
import type { Restaurant } from './restaurant';

/**
 * The branch every Eats screen is scoped to.
 *
 * The MERCHANT is no longer part of this scope: the auth service now issues it
 * as a JWT claim, so the backend derives it from the bearer token and no
 * endpoint takes a `merchant_id` param any more. What remains is the branch —
 * many endpoints still take an explicit `restaurant_id`, and which branch is
 * being viewed is a UI choice the token can't answer.
 *
 * It lives in THIS slice (not a separate `session` one) because it is the slice
 * that owns the API those ids address — a sibling entity may not import it, and
 * duplicating the branch query elsewhere would fork the cache.
 */
export interface MerchantScope {
  /** The branch currently being viewed, or `undefined` before branches load. */
  branch: Restaurant | undefined;
  /** Convenience alias for `branch?.id`. */
  branchId: string | undefined;
  /** Every branch owned by the merchant, for the branch picker. */
  branches: Restaurant[];
  /** Switch the active branch (persisted as a UI preference). */
  setBranchId: (id: string) => void;
  /** True once a branch has been resolved. */
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useMerchantScope(): MerchantScope {
  const activeBranchId = useUiStore((s) => s.activeBranchId);
  const setActiveBranchId = useUiStore((s) => s.setActiveBranchId);

  // The branch list is already narrowed to the caller's merchant by the token,
  // and the merchant-scoped endpoint always includes hidden branches.
  const query = useRestaurants();
  const branches = useMemo(() => query.data?.list ?? [], [query.data]);

  // A persisted branch id can outlive the branch itself (deleted, or the
  // merchant changed), so fall back to the first branch rather than trusting it.
  const branch = useMemo(
    () => branches.find((restaurant) => restaurant.id === activeBranchId) ?? branches[0],
    [branches, activeBranchId],
  );

  return {
    branch,
    branchId: branch?.id,
    branches,
    setBranchId: setActiveBranchId,
    isReady: branch !== undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
